/*
 * ******************************************************
 * Copyright (C) 1994-2021 Babiel GmbH <info@babiel.com>
 *
 * This file is part of a Babiel development
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted without the express
 * permission of Babiel GmbH
 *
 * Any modification is not permitted without the express
 * permission of Babiel GmbH
 *
 * The file may contain confidential and or privileged materials.
 * Any review, retransmission, dissemination or other use of
 * or taking of any action in reliance upon, this file
 * by persons or entities other than the intended is prohibited.
 * ******************************************************
 */

import Swiper, { A11y, Keyboard, Pagination } from 'swiper';
import { Component } from '../../componentSystem/component';
import { CarouselComponentConfig } from './carouselComponentConfig';
import { BreakpointService } from '../../services/breakpointService';

Swiper.use([A11y, Keyboard, Pagination]);

export class CarouselComponent extends Component {
  protected readonly config: CarouselComponentConfig = CarouselComponentConfig;

  private mainSwiperInstance: Swiper;
  private paginationSwiperInstance: Swiper;

  private static adaptSwiperIndex(masterSwiper: Swiper, slaveSwiper: Swiper) {
    const { realIndex } = masterSwiper;

    if (slaveSwiper.realIndex !== realIndex) {
      slaveSwiper.slideTo(realIndex);
    }
  }

  public init(): void {
    super.init();
    this.mainSwiperInit();
    this.paginationSwiperInitOrDestroy();
  }

  public setupEvents(): void {
    const { paginationItemSelector } = CarouselComponentConfig;
    const paginationItemElements = this.containerElement.querySelectorAll(paginationItemSelector);

    paginationItemElements.forEach((paginationItemElement, index) => {
      paginationItemElement.addEventListener('click', () => {
        this.slideTo(index);
      });
    });

    window.addEventListener('resize', () => {
      this.paginationSwiperInitOrDestroy();
    });
  }

  private mainSwiperInit(): void {
    const { mainSwiperContainerSelector, mainSwiperOptions } = CarouselComponentConfig;
    const mainSwiperContainer = this.containerElement.querySelector(mainSwiperContainerSelector) as HTMLElement;

    this.mainSwiperInstance = new Swiper(mainSwiperContainer, mainSwiperOptions);
    this.mainSwiperInstance.on('activeIndexChange', this.onMainSwiperActiveIndexChange.bind(this));
    this.onMainSwiperAfterInit();
  }

  private paginationSwiperInitOrDestroy(): void {
    if (BreakpointService.mediaBreakpointDown('md')) {
      if (!this.paginationSwiperInstance) {
        this.paginationSwiperInit();
      }
    } else {
      if (this.paginationSwiperInstance) {
        this.paginationSwiperDestroy();
      }
    }
  }

  private paginationSwiperInit(): void {
    const { paginationSwiperContainerSelector, paginationSwiperOptions } = CarouselComponentConfig;
    const paginationSwiperContainer = this.containerElement.querySelector(paginationSwiperContainerSelector) as HTMLElement;

    this.paginationSwiperInstance = new Swiper(paginationSwiperContainer, paginationSwiperOptions);
    this.paginationSwiperInstance.on('activeIndexChange', this.onPaginationSwiperActiveIndexChange.bind(this));
  }

  private paginationSwiperDestroy(): void {
    this.paginationSwiperInstance.destroy();
    this.paginationSwiperInstance = null;
  }

  private onMainSwiperAfterInit(): void {
    const { realIndex } = this.mainSwiperInstance;

    this.setPaginationItemActiveClass(realIndex);
  }

  private onMainSwiperActiveIndexChange(): void {
    const { realIndex, previousIndex } = this.mainSwiperInstance;

    if (this.paginationSwiperInstance) {
      CarouselComponent.adaptSwiperIndex(this.mainSwiperInstance, this.paginationSwiperInstance);
    }

    this.setPaginationItemActiveClass(realIndex, previousIndex);
  }

  private onPaginationSwiperActiveIndexChange(): void {
    CarouselComponent.adaptSwiperIndex(this.paginationSwiperInstance, this.mainSwiperInstance);
  }

  private setPaginationItemActiveClass(index: number, previousIndex?: number): void {
    const { paginationItemSelector, activeClass } = CarouselComponentConfig;
    const paginationItemElements = this.containerElement.querySelectorAll(paginationItemSelector);

    if (previousIndex !== undefined) {
      paginationItemElements[previousIndex].classList.remove(activeClass);
    }

    paginationItemElements[index].classList.add(activeClass);
  }

  private slideTo(index: number): void {
    this.mainSwiperInstance?.slideTo(index);
  }
}
